import { useState, useEffect, useCallback } from "react";
import { X, AlertTriangle, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { useUserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import {
  completeDiscordLink,
} from "../services/DiscordService";
import { toast } from "react-toastify";
import DiscordStatusModal from "./DiscordStatusModal";
import { useDiscordMembershipStatus } from "../hooks/useDiscordGate";

interface DiscordLinkProps {
  className?: string;
  iconSize?: number;
  compactMobile?: boolean;
  variant?: "default" | "light";
}

const DiscordIcon = ({ size = 18, className = "" }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0190 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1568 2.4189Z" />
  </svg>
);

export default function DiscordLink({
  className = "",
  iconSize = 20,
  compactMobile = false,
  variant = "default",
}: DiscordLinkProps) {
  const { user, load } = useUserContext();
  const { t } = useTranslation();
  const { hasIssue: hasDiscordIssue } = useDiscordMembershipStatus();

  const [showDiscordStatusModal, setShowDiscordStatusModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const isActive = !!(user?.retailers && user.retailers.length > 0);

  const handleCompleteDiscordLink = useCallback(
    async (discordUserId: string, discordRefreshToken: string) => {
      try {
        await completeDiscordLink({ discordUserId, discordRefreshToken });
        await load();
        toast.success(t("settings.discordLinkedSuccess"));
        setShowDiscordStatusModal(false);
        setTimeout(() => window.location.reload(), 1000);
      } catch (error) {
        console.error("Error completing Discord link:", error);
        toast.error(t("settings.discordLinkError"));
      }
    },
    [load, t]
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const discordLinkSuccess = urlParams.get("discord_link_success");
    const discordUserId = urlParams.get("discord_user_id");
    const discordRefreshToken = urlParams.get("discord_refresh_token");

    if (discordLinkSuccess === "true" && discordUserId && discordRefreshToken) {
      handleCompleteDiscordLink(discordUserId, discordRefreshToken);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [handleCompleteDiscordLink]);

  const handleDiscordAction = () => {
    if (!user) return;
    if (!isActive) {
      setShowUpgradeModal(true);
      return;
    }
    setShowDiscordStatusModal(true);
  };

  if (!user) return null;

  const discordLinked = !!(user.discordUserId && user.discordRefreshToken);

  return (
    <>
      <button
        onClick={handleDiscordAction}
        className={`relative flex items-center justify-center transition-all ${
          variant === "light"
            ? "p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-xl"
            : `shadow-lg ${
                compactMobile
                  ? "gap-1 px-1.5 py-1 md:gap-1.5 md:px-2.5 md:py-1.5"
                  : "gap-1.5 px-2.5 py-1.5"
              } text-xs font-medium rounded-lg ${
                discordLinked
                  ? "bg-[#5865F2] hover:bg-[#4752C4] text-white"
                  : "bg-primary-50 dark:bg-primary-900/30 hover:bg-primary-100 dark:hover:bg-primary-900/50 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800"
              }`
        } ${className}`}
        aria-label={discordLinked ? t("nav.discord") : t("nav.linkDiscord")}
      >
        <DiscordIcon size={iconSize} />
        {hasDiscordIssue && (
          <span
            className="absolute right-1 top-1 h-2.5 w-2.5 rounded-full bg-amber-400 ring-2 ring-primary-900 dark:ring-dark-900 shadow-[0_0_10px_rgba(251,191,36,0.7)]"
            aria-hidden="true"
          />
        )}
        {variant !== "light" && (
          <span className="whitespace-nowrap">Discord</span>
        )}
      </button>

      {/* Upgrade Required Modal (non-active users) */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-dark-800 rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Lock size={24} className="text-primary-600 dark:text-primary-400" />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Upgrade Required
                </h2>
              </div>
              <button
                onPointerDown={() => setShowUpgradeModal(false)}
                className="text-gray-400 dark:text-dark-400 hover:text-gray-600 dark:hover:text-dark-200 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  Discord access is available for active subscribers. Subscribe to a plan to unlock the DealHawk Discord community.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onPointerDown={() => setShowUpgradeModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg text-gray-700 dark:text-dark-200 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
                >
                  {t("common.cancel")}
                </button>
                <Link
                  to="/setup"
                  onPointerDown={() => setShowUpgradeModal(false)}
                  className="flex-1 px-4 py-2 rounded-lg font-medium text-sm bg-primary-600 text-white hover:bg-primary-700 transition-colors text-center"
                >
                  View Plans
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      <DiscordStatusModal
        isOpen={showDiscordStatusModal}
        onClose={() => setShowDiscordStatusModal(false)}
      />
    </>
  );
}
